// Stealth Clock — employee self-service shift control.
//
// Renders ONLY for stealth desktop-agent users in an org where a main_admin has turned
// the feature on. The backend decides both: GET /clock returns { enabled:false } (or
// isAgentUser:false) otherwise, and we render null in that case — so for every other
// org and every non-agent user this component is invisible and inert.
//
// WORDING IS DELIBERATE. "Start shift" / "End shift", never "start/stop tracking" and
// never "log in/out": background tracking is already running and is NOT affected by this
// button. The fixed sub-line says so explicitly, because a control that looks like it
// stops monitoring — but doesn't — is a consent problem, not a copy nit.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { Play, Square, Loader2, AlertCircle, AlertTriangle, ChevronDown } from 'lucide-react';
import { useDashTheme } from '../../pages/DashboardV2/hooks/useDashTheme';
import { getMyClock, punchClock } from './api';
import { isStealthClockEnabledForOrg } from './orgGating';
import { fmtTime, fmtDuration, fmtTimer, tzLabel } from './timeFmt';

const EXPANDED_KEY = 'tg.stealthClock.expanded';
const TARGET_SEC = 8 * 3600;

function readExpanded() {
  try { return localStorage.getItem(EXPANDED_KEY) === '1'; } catch { return false; }
}

function clockColors(t) {
  return {
    teal: t.isDark ? '#2DD4BF' : '#0F766E',
    tealMid: t.isDark ? '#14B8A6' : '#0D9488',
    tealSoft: t.isDark ? 'rgba(45, 212, 191, 0.14)' : 'rgba(15, 118, 110, 0.10)',
    stop: t.isDark ? '#F87171' : '#B42318',
  };
}

function ClockCardInner({ style }) {
  const t = useDashTheme();

  const [data, setData] = useState(null);      // null until first load resolves
  const [hidden, setHidden] = useState(false); // load failed or feature off → render nothing
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [, setTick] = useState(0);             // nonce: re-renders the running timer
  const [expanded, setExpanded] = useState(readExpanded);

  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  const load = useCallback(async () => {
    // Client-side pre-gate: when the org profile is already loaded and says the feature
    // is off, hide WITHOUT issuing a request — so a disabled org adds exactly zero
    // network calls to its dashboard. When the profile has not arrived yet
    // (window.__orgProfile absent), fall through and ask the server, which is always
    // authoritative. This is an optimisation, never the enforcement: every endpoint
    // re-checks the org flag server-side regardless of what the client believes.
    const profile = typeof window !== 'undefined' ? window.__orgProfile : null;
    if (profile && !isStealthClockEnabledForOrg(profile)) {
      setHidden(true);
      return;
    }
    try {
      const res = await getMyClock();
      if (!mountedRef.current) return;
      if (!res || res.enabled !== true || res.isAgentUser !== true) {
        setHidden(true);
        return;
      }
      setHidden(false);
      setData(res);
    } catch (_) {
      // Never render a broken widget: on any load failure the control hides itself,
      // exactly as if the feature were off.
      if (mountedRef.current) setHidden(true);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const isOpen = data?.state === 'in';
  const staleOpen = data?.stale_open === true;

  // Running timer. Ticks every second ONLY while a shift is open AND the tab is
  // visible — an off-duty or backgrounded card sets no interval at all. The displayed
  // value is recomputed from the shift's start instant on each tick (never
  // accumulated), so it is still exact the moment the tab comes back, without having
  // burned any cycles while hidden.
  const [visible, setVisible] = useState(
    typeof document === 'undefined' || document.visibilityState !== 'hidden'
  );
  useEffect(() => {
    const onVis = () => {
      const isVisible = document.visibilityState === 'visible';
      setVisible(isVisible);
      // Re-sync on return: another tab may have ended the shift entirely.
      if (isVisible) load();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [load]);

  useEffect(() => {
    if (!isOpen || !visible || staleOpen) return undefined;
    const id = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [isOpen, visible, staleOpen]);

  const onToggle = useCallback(async () => {
    if (saving || !data) return;
    const next = isOpen ? 'out' : 'in';
    setSaving(true);
    setError(null);
    try {
      await punchClock(next);
      await load();
    } catch (err) {
      const code = err?.response?.data?.code;
      if (code === 'ALREADY_IN_STATE') {
        // Server already in that state. Reload, then trust the 409 body if GET
        // still disagrees (stale open `in` older than the day lookback).
        const punchState = err?.response?.data?.state;
        const punchAt = err?.response?.data?.at;
        await load();
        if (punchState && mountedRef.current) {
          setData((prev) => {
            if (!prev || prev.state === punchState) return prev;
            return { ...prev, state: punchState, since: punchAt || prev.since };
          });
        }
      } else if (code === 'FEATURE_DISABLED') {
        setHidden(true);
      } else if (mountedRef.current) {
        setError(
          err?.response?.data?.error ||
            (next === 'in' ? "Couldn't start your shift." : "Couldn't end your shift.")
        );
      }
    } finally {
      if (mountedRef.current) setSaving(false);
    }
  }, [saving, data, isOpen, load]);

  const toggleExpanded = useCallback(() => {
    setExpanded((v) => {
      const next = !v;
      try { localStorage.setItem(EXPANDED_KEY, next ? '1' : '0'); } catch { /* ignore */ }
      return next;
    });
  }, []);

  if (hidden || !data) return null;

  // ── Day geometry + live segment maths ───────────────────────────────────────
  // Everything below is derived per render, so the open session grows with the timer
  // without any of it being stored in state.
  const dayStartMs = data.day_start ? new Date(data.day_start).getTime() : 0;
  const dayEndMs = data.day_end ? new Date(data.day_end).getTime() : dayStartMs + 86400000;

  const sessions = (data.segments || []).filter((s) => !(staleOpen && s.open)).map((s) => {
    const fromMs = new Date(s.from).getTime();
    // An open session is measured to NOW, but never past the day's end — so a shift
    // left open overnight stops growing at midnight instead of running away.
    const toMs = s.open ? Math.min(Date.now(), dayEndMs) : new Date(s.to).getTime();
    return {
      ...s,
      fromMs,
      toMs,
      liveSec: s.open ? Math.max(0, Math.floor((toMs - fromMs) / 1000)) : s.sec || 0,
    };
  });

  const openSeg = sessions.find((s) => s.open) || null;
  const liveOpenSec = openSeg ? openSeg.liveSec : 0;
  const displayTotalSec = sessions.reduce((sum, s) => sum + s.liveSec, 0);
  const events = data.events || [];
  const cc = clockColors(t);
  // The ARC is capped at a full circle — you cannot draw 261% of a ring — but the
  // NUMBER is not. Capping the label at 100% was hiding the very thing worth
  // seeing: a 20h shift reported "100% of 8h target" as if it were a good day.
  const targetPct = Math.round((displayTotalSec / TARGET_SEC) * 100);
  const arcPct = Math.min(100, targetPct);
  const overTarget = targetPct > 100;

  // Client-side safety net. `stale_open` comes from the server, and when it is
  // wrong (or lagging) an open shift keeps counting up forever and the card
  // presents it as normal. Anything past 16 hours open is not a work day — say
  // so here regardless of what the flag says, because the person looking at this
  // is the only one who can close it.
  const LONG_OPEN_SEC = 16 * 3600;
  const suspiciouslyLong = isOpen && liveOpenSec > LONG_OPEN_SEC;
  const maxSessionSec = Math.max(...sessions.map((s) => s.liveSec), 1);

  // Rail window: a readable default, widened only when someone works outside it.
  let railStart = 6 * 60;
  let railEnd = 22 * 60;
  for (const s of sessions) {
    const a = (s.fromMs - dayStartMs) / 60000;
    const b = (s.toMs - dayStartMs) / 60000;
    if (a < railStart) railStart = Math.floor(a / 60) * 60;
    if (b > railEnd) railEnd = Math.ceil(b / 60) * 60;
  }
  railStart = Math.max(0, railStart);
  railEnd = Math.min(24 * 60, railEnd);
  const pctOf = (ms) => {
    const min = (ms - dayStartMs) / 60000;
    const span = railEnd - railStart || 1;
    return ((Math.min(Math.max(min, railStart), railEnd) - railStart) / span) * 100;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={t.motion.spring.soft}
      style={{
        background: t.c.surface,
        border: `1px solid ${t.c.border}`,
        borderRadius: t.r.lg,
        boxShadow: t.shadow.sm,
        overflow: 'hidden',
        ...style,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap',
          padding: '14px 18px 12px',
        }}
      >
        <button
          type="button"
          onClick={toggleExpanded}
          aria-expanded={expanded}
          aria-label={expanded ? "Hide today's sessions" : "Show today's sessions"}
          style={{
            flex: 1,
            minWidth: 220,
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            background: 'none',
            border: 'none',
            padding: 0,
            textAlign: 'left',
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          <StatusPill t={t} on={isOpen} cc={cc} />
          {/* Timer over its own caption rather than beside it. On one row the
              caption had roughly 90px left in the popover and truncated to
              "Started 12:00 …", which is the one part someone actually reads. */}
          <span style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0, alignItems: 'flex-start' }}>
            <span
              style={{
                fontSize: 22,
                fontWeight: 700,
                letterSpacing: '-0.03em',
                fontFamily: t.font.numeric,
                fontVariantNumeric: 'tabular-nums',
                color: suspiciouslyLong ? cc.stop : t.c.text,
                lineHeight: 1,
              }}
            >
              {staleOpen ? '—' : isOpen ? fmtTimer(liveOpenSec) : fmtDuration(displayTotalSec)}
            </span>
            <span
              style={{
                fontSize: t.font.size.sm,
                color: t.c.textSecondary,
                maxWidth: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {staleOpen
                ? 'On shift · nothing recorded today'
                : isOpen
                ? <>Started {fmtTime(openSeg?.from, data.tz)} · <b style={{ color: t.c.text, fontWeight: 600 }}>{fmtDuration(displayTotalSec)}</b> tracked today</>
                : events.length
                  ? <>Last ended {fmtTime(events[events.length - 1].at, data.tz)} · <b style={{ color: t.c.text, fontWeight: 600 }}>{fmtDuration(displayTotalSec)}</b> tracked</>
                  : 'Nothing recorded today'}
            </span>
          </span>
        </button>

        <button
          type="button"
          onClick={onToggle}
          disabled={saving}
          data-long-open={suspiciouslyLong ? '1' : undefined}
          style={{
            border: isOpen ? `1px solid ${t.c.border}` : 'none',
            background: isOpen ? t.c.surface : t.c.primaryGradient,
            color: isOpen ? cc.stop : '#fff',
            boxShadow: isOpen ? 'none' : `0 6px 18px ${t.c.primaryRing}`,
            borderRadius: 10,
            height: 34,
            padding: '0 12px',
            fontSize: t.font.size.sm,
            fontWeight: t.font.weight.semibold,
            fontFamily: 'inherit',
            cursor: saving ? 'default' : 'pointer',
            opacity: saving ? 0.7 : 1,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            flexShrink: 0,
          }}
        >
          {saving ? (
            <Loader2 size={13} className="animate-spin" />
          ) : isOpen ? (
            <Square size={10} fill="currentColor" />
          ) : (
            <Play size={12} fill="currentColor" />
          )}
          {isOpen ? 'End shift' : 'Start shift'}
        </button>

        <button
          type="button"
          onClick={toggleExpanded}
          aria-hidden
          tabIndex={-1}
          style={{
            width: 34,
            height: 34,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: `1px solid ${t.c.border}`,
            borderRadius: 10,
            background: t.c.surface,
            color: t.c.textSecondary,
            cursor: 'pointer',
            flexShrink: 0,
          }}
        >
          <ChevronDown
            size={16}
            style={{ transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 180ms' }}
          />
        </button>
      </div>

      {!expanded && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            padding: '10px 18px 12px',
            borderTop: `1px solid ${t.c.border}`,
            fontSize: t.font.size.sm,
            color: t.c.textTertiary,
          }}
        >
          <span>
            {sessions.length > 0
              ? `${sessions.length} ${sessions.length === 1 ? 'session' : 'sessions'} · auto-tracking on`
              : 'auto-tracking on'}
          </span>
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>
            <b style={{ color: overTarget ? cc.stop : t.c.textSecondary, fontWeight: 600 }}>{fmtDuration(displayTotalSec)}</b>
            {' '}of 8h target
            {overTarget ? <span style={{ color: cc.stop, fontWeight: 600 }}>{' '}· {targetPct}%</span> : null}
          </span>
        </div>
      )}

      {/* A shift open past 16 hours is not a work day — it is one nobody closed.
          The server has a `stale_open` flag for this, but it has been observed
          not firing, and an open clock that quietly counts past 20 hours is the
          kind of thing only the person looking at it can fix. So the card says
          it on its own evidence rather than waiting to be told. */}
      {suspiciouslyLong && (
        <div
          role="status"
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 9,
            margin: '0 18px 12px',
            padding: '9px 12px',
            borderRadius: t.r.md,
            background: t.status.warningSoft || 'rgba(217,119,6,.12)',
            color: t.status.warning || '#b45309',
            fontSize: t.font.size.sm,
            lineHeight: 1.45,
          }}
        >
          <AlertTriangle size={15} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>
            This shift has been open for <b style={{ fontWeight: 600 }}>{fmtDuration(liveOpenSec)}</b>.
            If you finished earlier, end it now — the timer keeps running until you do.
          </span>
        </div>
      )}

      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: t.s[2],
              padding: `0 ${t.s[5]}px ${t.s[3]}px`,
              color: t.status.negative,
              fontSize: t.font.size.sm,
            }}
          >
            <AlertCircle size={14} /> {error}
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            key="clock-details"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
            style={{ overflow: 'hidden' }}
          >
            <div style={{ padding: `4px 20px 16px` }}>
              {sessions.length === 0 && (data.anomalies || []).length === 0 ? (
                <div style={{ padding: `${t.s[4]}px 0`, fontSize: t.font.size.sm, color: t.c.textTertiary }}>
                  No shift recorded yet today.
                </div>
              ) : (
                <div
                  style={{
                    display: 'flex',
                    gap: 28,
                    alignItems: 'flex-start',
                    flexWrap: 'wrap',
                  }}
                >
                  <ProgressRing
                    t={t}
                    cc={cc}
                    pct={arcPct}
                    pctLabel={targetPct}
                    over={overTarget}
                    timerLabel={staleOpen ? '—' : isOpen ? fmtTimer(liveOpenSec) : fmtDuration(displayTotalSec)}
                  />

                  <div style={{ flex: 1, minWidth: 260 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                      <span
                        style={{
                          fontSize: t.font.size.xs,
                          fontWeight: t.font.weight.semibold,
                          letterSpacing: '0.08em',
                          textTransform: 'uppercase',
                          color: t.c.textTertiary,
                        }}
                      >
                        Today · {tzLabel(data.tz)}
                      </span>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 12, fontSize: t.font.size.xs, color: t.c.textTertiary }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                          <span style={{ width: 7, height: 7, borderRadius: 99, background: cc.tealMid, opacity: 0.45 }} />
                          Completed
                        </span>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                          <span style={{ width: 7, height: 7, borderRadius: 99, background: cc.teal }} />
                          Running
                        </span>
                      </span>
                    </div>

                    <DayRail
                      t={t}
                      cc={cc}
                      sessions={sessions}
                      pctOf={pctOf}
                      railStart={railStart}
                      railEnd={railEnd}
                      dayStartMs={dayStartMs}
                      tz={data.tz}
                    />

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 14 }}>
                      {sessions.map((s, i) => (
                        <SessionRow
                          key={`${s.from}-${i}`}
                          t={t}
                          cc={cc}
                          index={i + 1}
                          from={fmtTime(s.from, data.tz)}
                          to={s.open ? null : fmtTime(s.to, data.tz)}
                          open={s.open}
                          durationLabel={s.open ? fmtTimer(s.liveSec) : fmtDuration(s.liveSec)}
                          barPct={(s.liveSec / maxSessionSec) * 100}
                          clippedStart={s.clipped_start}
                        />
                      ))}
                    </div>

                    {(data.anomalies || []).map((a, i) => (
                      <AnomalyRow key={`a-${i}`} t={t} type={a.type} time={fmtTime(a.at, data.tz)} />
                    ))}
                  </div>
                </div>
              )}

              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginTop: 16,
                  paddingTop: 12,
                  borderTop: `1px solid ${t.c.border}`,
                  gap: t.s[3],
                }}
              >
                <span style={{ fontSize: t.font.size.sm, color: t.c.textTertiary }}>
                  {sessions.length > 0
                    ? `${sessions.length} ${sessions.length === 1 ? 'session' : 'sessions'} · auto-tracking on`
                    : 'Marks hours only · auto-tracking on'}
                </span>
                <span
                  style={{
                    marginLeft: 'auto',
                    fontSize: t.font.size.sm,
                    color: t.c.textSecondary,
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  <b style={{ color: t.c.text, fontWeight: 600 }}>{fmtDuration(displayTotalSec)}</b>
                  {' '}of 8h target
                </span>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

function StatusPill({ t, on, cc }) {
  const color = on ? cc.teal : t.status.neutral;
  const bg = on ? cc.tealSoft : t.status.neutralSoft;
  // An infinite animation keeps requestAnimationFrame alive for as long as the shift
  // is open. Honour the OS "reduce motion" setting — it costs nothing to respect and
  // removes the only continuous work this card does.
  const reduce = useReducedMotion();
  const animated = on && !reduce;
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 7,
        color,
        background: bg,
        fontSize: t.font.size.sm,
        fontWeight: t.font.weight.semibold,
        padding: '5px 11px 5px 9px',
        borderRadius: t.r.pill,
        // "On duty" was breaking across two lines in the narrow popover.
        whiteSpace: 'nowrap',
        flexShrink: 0,
      }}
    >
      <motion.span
        animate={animated ? { opacity: [1, 0.45, 1], scale: [1, 0.82, 1] } : { opacity: 1, scale: 1 }}
        transition={animated ? { duration: 2.4, repeat: Infinity, ease: 'easeInOut' } : { duration: 0 }}
        style={{
          width: 7,
          height: 7,
          borderRadius: '50%',
          background: 'currentColor',
          // Compositor-only hint: opacity+transform never trigger layout or paint.
          willChange: animated ? 'opacity, transform' : 'auto',
        }}
      />
      {on ? 'On duty' : 'Off duty'}
    </span>
  );
}

/**
 * The day as one horizontal bar. Gives shape to a broken-up day (three short stints
 * read very differently here than as three lines of text) before the exact times.
 */
function ProgressRing({ t, cc, pct, pctLabel, timerLabel, over }) {
  const size = 168;
  const stroke = 11;
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const dash = (Math.min(Math.max(pct, 0), 100) / 100) * circ;
  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={t.c.surfaceMuted} strokeWidth={stroke} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={over ? cc.stop : cc.teal}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={`${dash} ${circ}`}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div
          style={{
            fontSize: 22,
            fontWeight: 700,
            letterSpacing: '-0.03em',
            fontFamily: t.font.numeric,
            fontVariantNumeric: 'tabular-nums',
            color: t.c.text,
          }}
        >
          {timerLabel}
        </div>
        <div
          style={{
            fontSize: 11,
            // Past the target the ring is full either way, so the COLOUR is the
            // only thing left to say "this is not a normal day".
            color: over ? cc.stop : cc.teal,
            marginTop: 2,
            fontWeight: 600,
          }}
        >
          {(pctLabel ?? pct)}% of 8h target
        </div>
      </div>
    </div>
  );
}

function DayRail({ t, cc, sessions, pctOf, railStart, railEnd, dayStartMs, tz }) {
  const labelAt = (min) => fmtTime(new Date(dayStartMs + min * 60000), tz);
  const nowPct = pctOf(Date.now());
  const ticks = [];
  for (let m = railStart; m <= railEnd; m += 240) ticks.push(m);
  if (ticks[ticks.length - 1] !== railEnd) ticks.push(railEnd);
  const nowLabel = fmtTime(new Date(), tz);
  const tipLeft = Math.min(Math.max(nowPct, 8), 92);

  return (
    <div>
      <div style={{ position: 'relative', height: 36 }}>
        {sessions.some((s) => s.open) && (
          <span
            style={{
              position: 'absolute',
              top: 0,
              left: `${tipLeft}%`,
              transform: 'translateX(-50%)',
              background: cc.teal,
              color: '#fff',
              fontSize: 10,
              fontWeight: 600,
              padding: '2px 6px',
              borderRadius: 6,
              whiteSpace: 'nowrap',
              fontVariantNumeric: 'tabular-nums',
              zIndex: 2,
            }}
          >
            {nowLabel}
          </span>
        )}
        <div
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: 20,
            height: 10,
            background: t.c.surfaceMuted,
            borderRadius: 99,
          }}
        />
        {sessions.map((s, i) => {
          const left = pctOf(s.fromMs);
          const width = Math.max(pctOf(s.toMs) - left, 1.2);
          return (
            <motion.div
              key={`${s.from}-${i}`}
              initial={{ opacity: 0, scaleX: 0.8 }}
              animate={{ opacity: 1, scaleX: 1 }}
              transition={{ ...t.motion.spring.snappy, delay: i * 0.04 }}
              style={{
                position: 'absolute',
                top: 20,
                height: 10,
                left: `${left}%`,
                width: `${width}%`,
                borderRadius: 99,
                transformOrigin: 'left center',
                background: s.open ? cc.teal : cc.tealMid,
                opacity: s.open ? 1 : 0.55,
              }}
            />
          );
        })}
        {sessions.some((s) => s.open) && (
          <span
            style={{
              position: 'absolute',
              top: 16,
              left: `${nowPct}%`,
              width: 2,
              height: 18,
              marginLeft: -1,
              borderRadius: 99,
              background: cc.teal,
              zIndex: 1,
            }}
          />
        )}
      </div>
      <div
        style={{
          position: 'relative',
          height: 16,
          marginTop: 4,
          fontSize: t.font.size.xs,
          color: t.c.textTertiary,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {ticks.map((min, i) => {
          const span = railEnd - railStart || 1;
          const left = ((min - railStart) / span) * 100;
          const align = i === 0 ? 'left' : i === ticks.length - 1 ? 'right' : 'center';
          return (
            <span
              key={min}
              style={{
                position: 'absolute',
                left: `${left}%`,
                transform: align === 'center' ? 'translateX(-50%)' : align === 'right' ? 'translateX(-100%)' : 'none',
              }}
            >
              {labelAt(min)}
            </span>
          );
        })}
      </div>
    </div>
  );
}

/**
 * One completed-or-running SESSION, not one raw click.
 *
 * The duration lives in its own right-hand column and the wall-clock times stay in the
 * left column — a running "9:46" must never appear where a reader expects a time of day.
 */
function SessionRow({ t, cc, index, from, to, open, durationLabel, barPct, clippedStart }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '10px 12px',
        borderRadius: 12,
        background: open ? cc.tealSoft : t.c.surface,
        border: `1px solid ${open ? 'transparent' : t.c.border}`,
      }}
    >
      <span
        style={{
          width: 22,
          height: 22,
          flex: 'none',
          borderRadius: 99,
          background: open ? cc.teal : t.c.surfaceMuted,
          color: open ? '#fff' : t.c.textTertiary,
          fontSize: 11,
          fontWeight: 600,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {index}
      </span>

      <span
        style={{
          fontSize: t.font.size.sm,
          fontWeight: 500,
          fontVariantNumeric: 'tabular-nums',
          color: t.c.text,
          whiteSpace: 'nowrap',
        }}
      >
        {clippedStart && (
          <span title="Started the previous day" style={{ color: t.c.textTertiary }}>⤷ </span>
        )}
        {from}
        <span style={{ color: t.c.textTertiary, margin: '0 6px' }}>→</span>
        {to || <span style={{ color: cc.teal, fontWeight: 600 }}>now</span>}
      </span>

      <span
        style={{
          flex: 1,
          height: open ? 6 : 4,
          borderRadius: 99,
          background: t.c.surfaceMuted,
          overflow: 'hidden',
          minWidth: 40,
        }}
      >
        <span
          style={{
            display: 'block',
            height: '100%',
            width: `${Math.max(barPct, open ? 8 : 2)}%`,
            borderRadius: 99,
            background: cc.teal,
            opacity: open ? 1 : 0.45,
          }}
        />
      </span>

      <span
        style={{
          fontSize: t.font.size.sm,
          fontWeight: 600,
          fontVariantNumeric: 'tabular-nums',
          color: open ? cc.teal : t.c.textSecondary,
          minWidth: 44,
          textAlign: 'right',
        }}
      >
        {durationLabel}
      </span>
    </div>
  );
}

/** A stray clock-out or a duplicate start — shown, never counted. */
function AnomalyRow({ t, type, time }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: t.s[2],
        marginTop: t.s[2],
        fontSize: t.font.size.sm,
        color: t.status.warning,
      }}
    >
      <AlertCircle size={13} />
      {type === 'orphan_out'
        ? `Stray end at ${time} — no matching start, not counted`
        : `Duplicate start at ${time} — ignored`}
    </div>
  );
}

/**
 * Containment boundary — the reason this module cannot break the dashboard.
 *
 * DashboardV2 is NOT wrapped in an error boundary, so an uncaught render error in any
 * child would blank the whole page. This boundary makes the worst case of the shift
 * clock "the card is not there" — which is exactly its disabled state — instead of
 * "the dashboard is gone". It renders null on ANY error and never rethrows.
 *
 * Deliberately local to this module rather than reusing components/ErrorBoundary:
 * that one renders a visible fallback UI, which is wrong for an optional, per-org
 * widget that is invisible for most organizations anyway.
 */
class ClockBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error, info) {
    // Logged, never surfaced — the host page must not learn about this.
    // eslint-disable-next-line no-console
    console.error('[stealth-clock] card suppressed after render error:', error, info);
  }

  render() {
    return this.state.failed ? null : this.props.children;
  }
}

export function ClockCard(props) {
  return (
    <ClockBoundary>
      <ClockCardInner {...props} />
    </ClockBoundary>
  );
}

export default ClockCard;
